// How the timer works: lengths, breaks and the alarm. Each device keeps its own: the alarm in
// particular belongs to the device that rings.

export const ALARM_SOUNDS = [
  { value: 'campanella', label: 'Campanella' },
  { value: 'bip', label: 'Bip' },
  { value: 'gong', label: 'Gong' },
  { value: 'marimba', label: 'Marimba' },
] as const;
export type AlarmSound = (typeof ALARM_SOUNDS)[number]['value'];

/** How long the alarm rings, in seconds; 0 = until stopped (two minutes at most). */
export const RING_OPTIONS = [
  { value: '5', label: '5 secondi' },
  { value: '15', label: '15 secondi' },
  { value: '30', label: '30 secondi' },
  { value: '0', label: 'Finché non la fermi' },
] as const;
export const RING_UNTIL_STOPPED_MAX = 120;

export type TimerSettings = {
  focusMinutes: number;
  shortBreak: number;
  longBreak: number;
  /** A long break after this many sessions of the day. */
  longEvery: number;
  /** The break starts by itself when the focus ends. */
  autoBreak: boolean;
  /** The next focus starts by itself when the break ends. */
  autoFocus: boolean;
  alarm: boolean;
  sound: AlarmSound;
  /** 0–100. */
  volume: number;
  ringSeconds: number;
};

export const DEFAULT_SETTINGS: TimerSettings = {
  focusMinutes: 25,
  shortBreak: 5,
  longBreak: 15,
  longEvery: 4,
  autoBreak: true,
  autoFocus: false,
  alarm: true,
  sound: 'campanella',
  volume: 70,
  ringSeconds: 15,
};

export const LIMITS = {
  focusMinutes: [1, 240],
  shortBreak: [1, 60],
  longBreak: [1, 90],
  longEvery: [2, 8],
  volume: [0, 100],
} as const;

const whole = (value: unknown, [min, max]: readonly [number, number], fallback: number) => {
  const number = Math.round(Number(value));
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
};

/** Settings from anything stored: unknown values fall back to the defaults, numbers stay in range. */
export function parseSettings(raw: unknown): TimerSettings {
  const value = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const flag = (key: keyof TimerSettings) =>
    typeof value[key] === 'boolean' ? (value[key] as boolean) : (DEFAULT_SETTINGS[key] as boolean);
  const sound = ALARM_SOUNDS.find((option) => option.value === value.sound)?.value;
  const ring = RING_OPTIONS.find((option) => Number(option.value) === value.ringSeconds);
  return {
    focusMinutes: whole(value.focusMinutes, LIMITS.focusMinutes, DEFAULT_SETTINGS.focusMinutes),
    shortBreak: whole(value.shortBreak, LIMITS.shortBreak, DEFAULT_SETTINGS.shortBreak),
    longBreak: whole(value.longBreak, LIMITS.longBreak, DEFAULT_SETTINGS.longBreak),
    longEvery: whole(value.longEvery, LIMITS.longEvery, DEFAULT_SETTINGS.longEvery),
    autoBreak: flag('autoBreak'),
    autoFocus: flag('autoFocus'),
    alarm: flag('alarm'),
    sound: sound ?? DEFAULT_SETTINGS.sound,
    volume: whole(value.volume, LIMITS.volume, DEFAULT_SETTINGS.volume),
    ringSeconds: ring ? Number(ring.value) : DEFAULT_SETTINGS.ringSeconds,
  };
}

/** A break in progress. Breaks are not synced: they belong to the device you rest in front of. */
export type BreakState = {
  startedAt: string;
  minutes: number;
  long: boolean;
  /** What the focus before was about, for the next one. */
  taskId: string | null;
  label: string;
};

export const breakEnd = (state: BreakState) =>
  new Date(state.startedAt).getTime() + state.minutes * 60_000;

/** The break after the `done`-th session of the day: long every few sessions, short otherwise. */
export function breakAfter(done: number, settings: TimerSettings) {
  const long = done > 0 && done % settings.longEvery === 0;
  return { long, minutes: long ? settings.longBreak : settings.shortBreak };
}

/** Where the day is in the cycle: "2 of 4" before the long break. */
export function cyclePosition(done: number, settings: TimerSettings) {
  const inCycle = done % settings.longEvery;
  return { done: done > 0 && inCycle === 0 ? settings.longEvery : inCycle, of: settings.longEvery };
}
